import { FC } from 'react';
import { MessageCircle, Phone, Users, Clock, User, Building } from 'lucide-react';
import { CustomerInteractionRecord } from '../../store/slices/interactionsSlice';

interface InteractionScheduleItemProps {
  interaction: CustomerInteractionRecord;
  onClick?: (interaction: CustomerInteractionRecord) => void;
  className?: string;
}

export const InteractionScheduleItem: FC<InteractionScheduleItemProps> = ({
  interaction,
  onClick,
  className = ""
}) => {
  const getInteractionIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      case 'phone call':
        return <Phone className="w-4 h-4 text-primary" />;
      case 'visit/meeting':
      case 'visit':
      case 'meeting':
        return <Users className="w-4 h-4 text-purple-600" />;
      default:
        return <MessageCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'low':
        return 'border-l-green-500 bg-green-50';
      default:
        return 'border-l-gray-500 bg-background';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick(interaction);
    }
  };

  return (
    <div
      className={`p-3 border-l-4 rounded-lg cursor-pointer transition-all hover:shadow-md ${getPriorityColor(interaction.priority)} ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getInteractionIcon(interaction.interaction_type)}
          <span className="font-medium text-sm text-muted-foreground">
            {interaction.interaction_type || 'Interaction'}
          </span>
          {/* Priority badge removed */}
        </div>
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>{formatTime(interaction.date_created)}</span>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <User className="w-3 h-3 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {interaction.customer_name}
          </span>
        </div>
        
        {interaction.tambak_name && (
          <div className="flex items-center space-x-2">
            <Building className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {interaction.tambak_name}
            </span>
          </div>
        )}

        {interaction.keterangan && (
          <div className="mt-2 text-xs text-muted-foreground bg-muted/60 p-2 rounded">
            {interaction.keterangan.length > 100 
              ? `${interaction.keterangan.substring(0, 100)}...` 
              : interaction.keterangan
            }
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
        <div className="text-xs text-muted-foreground">
          Customer: {interaction.No_pelanggan}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatDate(interaction.date_created)}
        </div>
      </div>
    </div>
  );
};